// Helpers for formatting event dates without timezone-shifting.
//
// Event dates are logically "calendar dates" — Aug 8th at 4pm London time
// means Aug 8th regardless of who's looking. But our stored ISO strings
// look like "2026-08-08T00:00:00Z" or "2026-08-08T15:00:00.000+00:00",
// which `new Date(...).toLocaleDateString()` shifts by the *viewer's*
// timezone offset. Result: a Californian sees "Aug 7".
//
// These helpers pull Y/M/D straight out of the ISO string as authored and
// reconstruct a local-timezone Date object, so the calendar date renders
// identically everywhere.

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse the calendar portion of a stored event date, ignoring any time/TZ
 * suffix. Returns `null` for empty/invalid input.
 */
export function parseEventCalendarDate(iso) {
  if (!iso) return null;
  const m = String(iso).match(ISO_DATE_RE);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
  };
}

/** Return a Date pinned to the local timezone at midnight of the event's
 * calendar day — safe to pass into `toLocaleDateString` without drift. */
export function eventCalendarDate(iso) {
  const parts = parseEventCalendarDate(iso);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day);
}

/**
 * Format an event's calendar date consistently across viewer timezones.
 * Falls back to an empty string on invalid input.
 */
export function formatEventDate(iso, options = { weekday: "short", day: "numeric", month: "short" }, locale = "en-GB") {
  const d = eventCalendarDate(iso);
  if (!d) return "";
  return d.toLocaleDateString(locale, options);
}
